import type {
	MacroregionIdentifier,
	SearchParams,
	SearchSuggestFieldProps,
	Suggestion,
	SuggestionCondoResult
} from '~/components/SearchSuggest';
import type { SuggestionGroup } from '~/composables/useLocationAutocomplete';
import type { LocationProviders } from '~~/core/ports/repositories/LocationAutocomplete';

import { useEventBus } from '@vueuse/core';
import { useResearchsSuggest } from '~/composables/useResearchsSuggest';
import { EventBusKeys } from '~/core/helpers/symbols';
import { AddressService } from '~/services/AddressService';
import { v4 as uuidv4 } from 'uuid';

export type SearchSelectionEvent =
	| { type: 'address'; payload: SelectAddressEvent }
	| { type: 'place'; payload: SelectAddressEvent }
	| { type: 'condo'; payload: SuggestionCondoResult }
	| { type: 'code'; payload: Suggestion['searchParams'] };

export const searchSuggestBus = useEventBus(
	EventBusKeys.searchSuggestSelection
);

export type SelectAddressEvent = Suggestion['searchParams'] & {
	searchType?: Suggestion['searchType'];
	macroregionUsed?: Suggestion['macroregionUsed'];
	title?: string;
	subtitle?: string;
};

export type SelectCondoEvent = SelectAddressEvent & {
	condoName: string;
};

/** Bus para emitir seleções do SearchSuggest. Default: searchSuggestBus global */
export interface SearchSuggestSelectionBus {
	emit(event: SearchSelectionEvent): void;
}

export type SearchSuggestDialogEmits = {
	(e: 'select-address', value: SelectAddressEvent): void;
	(e: 'select-place', value: Suggestion['searchParams']): void;
	(e: 'select-condo', value: SuggestionCondoResult): void;
	(e: 'select-code', value: Suggestion['searchParams']): void;
	(e: 'update:search-term', value: string): void;
	(e: 'emptyResultOnFilter', searchTerm: string): void;
	(e: 'clear'): void;
	(e: 'close-modal'): void;
};

interface UseNewSearchSuggestOptions {
	props: SearchSuggestFieldProps;
	emit: SearchSuggestDialogEmits;
	isDropdownOpen?: Ref<boolean>;
	providers?: LocationProviders[];
	macroregion?: MaybeRefOrGetter<string | undefined>;
	selectOnly?: boolean;
	/** Bus para emitir seleções quando selectOnly=false. Default: searchSuggestBus */
	bus?: SearchSuggestSelectionBus;
}

function extractStateFromSubtitle(subtitle: string): string | undefined {
	const parts = subtitle.split('-').map(p => p.trim());
	const lastPart = parts[parts.length - 1];
	if (lastPart && lastPart.length === 2 && lastPart.match(/^[A-Z]{2}$/)) {
		return lastPart;
	}
	return undefined;
}

function ensureStatesInSearchParams(suggestion: Suggestion): SearchParams {
	const searchParams = { ...suggestion.searchParams };

	if (!searchParams.states && suggestion.result.subtitle) {
		const extractedState = extractStateFromSubtitle(suggestion.result.subtitle);
		if (extractedState) {
			searchParams.states = [extractedState];
		}
	}

	return searchParams;
}

// Changed from async to sync - no top-level await needed
// Internal async functions still work correctly
export function useNewSearchSuggest({
	props,
	emit,
	isDropdownOpen,
	providers,
	macroregion,
	selectOnly = false,
	bus = searchSuggestBus
}: UseNewSearchSuggestOptions) {
	const {
		filteredLastResearchs: rawFilteredLastResearchs,
		updateResearchs,
		removeResearchs
	} = useResearchsSuggest();

	const filteredLastResearchs = computed<Suggestion[]>(() => {
		const list = rawFilteredLastResearchs.value;
		if (props.excludeStreetAndPoiFromLastResearchs !== true) return list;
		return list.filter(
			s => s.searchType !== 'street' && s.searchType !== 'point_of_interest'
		);
	});

	const router = useRouter();

	// Reactive state - shared between components
	const searchTerm = shallowRef<string>(props.searchTerm || '');
	const modelValue = ref<Suggestion>();
	const commandEl = useTemplateRef<HTMLElement>('commandEl');
	const hasSelectedValue = shallowRef<boolean>(false);
	const isNavigatingWithKeyboard = ref<boolean>(false);
	const isUpdatingFromWatch = ref<boolean>(false);
	const backupSearchTerm = ref<string>('');
	const isInternalLoading = ref<boolean>(false);
	const isTransitionBlocked = ref<boolean>(false);

	const hasMinSearchTerm = computed<boolean>(
		() => searchTerm.value.length >= 3
	);

	// Convert macroregion to ref for useLocationAutocomplete
	const macroregionRef = computed(() => toValue(macroregion));

	const {
		data: locationAutocompleteData,
		loading: apiLoading,
		execute: executeLocationAutocomplete
	} = useLocationAutocomplete(searchTerm, {
		providers,
		hasSelectedValue,
		isDropdownOpen,
		maxNumber: { cities: 6 },
		macroregion: macroregionRef
	});

	const loading = computed(() => apiLoading.value || isInternalLoading.value);

	const shouldShowCodeGroup = computed<boolean>(() => {
		const regex = /^[a-zA-Z0-9]{2,5}(\d{1,8})?$/m;
		const isValidCode = regex.test(searchTerm.value);
		return isValidCode && hasMinSearchTerm.value;
	});

	const searchByCodeOption = computed<Suggestion[]>(() => {
		const value = searchTerm.value.toUpperCase();

		if (
			!hasMinSearchTerm.value ||
			!shouldShowCodeGroup.value ||
			loading.value
		) {
			return [];
		}

		return [
			{
				result: {
					title: value,
					subtitle: ''
				},
				id: uuidv4(),
				searchParams: {
					code: value,
					regions: [],
					cities: [],
					macroregions: [],
					placeId: null,
					condoSlug: null
				},
				searchType: 'code'
			}
		];
	});

	const shouldShowEmptyState = computed<boolean>(
		() =>
			!locationAutocompleteData.value?.length &&
			!loading.value &&
			hasMinSearchTerm.value &&
			(!shouldShowCodeGroup.value || !searchByCodeOption.value.length)
	);

	watch(shouldShowEmptyState, isEmpty => {
		if (isEmpty) {
			emit('emptyResultOnFilter', searchTerm.value);
		}
	});

	const shouldShowLastResearchs = computed<boolean>(() => {
		const hasLastResearchs = filteredLastResearchs.value.length > 0;

		if (loading.value) {
			return false;
		}

		if (
			(!hasMinSearchTerm.value || shouldShowEmptyState.value) &&
			!shouldShowCodeGroup.value
		) {
			return hasLastResearchs;
		}

		return (
			hasLastResearchs &&
			!searchByCodeOption.value.length &&
			locationAutocompleteData.value?.length === 0
		);
	});

	// Google Maps API - lazy loading
	const { init: initGoogleMaps } = useGoogleMaps();

	// Shared functions
	function clear(emitClear: boolean = true) {
		isUpdatingFromWatch.value = true;
		backupSearchTerm.value = '';
		modelValue.value = undefined;
		searchTerm.value = '';
		hasSelectedValue.value = false;
		isInternalLoading.value = false;
		if (emitClear) {
			emit('clear');
		}
	}

	function clearLocationData() {
		if (searchTerm.value === '') {
			isInternalLoading.value = false;
		}
	}

	function handleKeyDown(e: KeyboardEvent) {
		isNavigatingWithKeyboard.value = true;
	}

	// Computed que retorna o placeholder dinâmico baseado nos filtros atuais
	const dynamicPlaceholder = computed(() => {
		// Acessar as propriedades diretamente para garantir reatividade profunda
		const filters = props.currentFilters;
		const macroregions = filters?.macroregions;
		const cities = filters?.cities;
		const regions = filters?.regions;
		const addressSlug = filters?.addressSlug;
		const poiLocationLat = filters?.poiLocationLat;
		const poiLocationLng = filters?.poiLocationLng;
		const states = filters?.states;

		// Prioridade: city > macroregion > region > addressSlug > poi
		if (cities && cities.length > 0) {
			const city = cities[0];
			const uf = states?.[0] || '';
			return `Busque lugares em ${city}${uf ? ` - ${uf}` : ''}`;
		}

		if (macroregions && macroregions.length > 0) {
			const macroregion = macroregions[0];
			const uf = states?.[0] || '';
			return `Busque lugares em ${macroregion}${uf ? ` - ${uf}` : ''}`;
		}

		if (regions && regions.length > 0) {
			if (cities && cities.length > 0) {
				const city = cities[0];
				const uf = states?.[0] || '';
				return `Busque lugares em ${city}${uf ? ` - ${uf}` : ''}`;
			}

			const region = regions[0];
			const uf = states?.[0] || '';
			return `Busque lugares em ${region}${uf ? ` - ${uf}` : ''}`;
		}

		// Para POI ou addressSlug, extrair cidade do próprio addressSlug
		if (poiLocationLat && poiLocationLng && addressSlug) {
			const { city: extractedCity, state: extractedState } =
				AddressService.extractCityFromAddressSlug(addressSlug);

			if (extractedCity) {
				return `Busque lugares em ${extractedCity}${extractedState ? ` - ${extractedState}` : ''}`;
			}

			// Fallback: tentar usar cities do filtro
			if (cities && cities.length > 0) {
				const city = cities[0];
				const uf = states?.[0] || '';
				return `Busque lugares em ${city}${uf ? ` - ${uf}` : ''}`;
			}

			const uf = states?.[0] || '';
			return `Busque lugares em ${uf || ''}`;
		}

		if (addressSlug) {
			// Para endereço, extrair cidade do addressSlug
			const { city: extractedCity, state: extractedState } =
				AddressService.extractCityFromAddressSlug(addressSlug);

			if (extractedCity) {
				return `Busque lugares em ${extractedCity}${extractedState ? ` - ${extractedState}` : ''}`;
			}

			// Fallback: tentar usar cities do filtro
			if (cities && cities.length > 0) {
				const city = cities[0];
				const uf = states?.[0] || '';
				return `Busque lugares em ${city}${uf ? ` - ${uf}` : ''}`;
			}

			const uf = states?.[0] || '';
			return `Busque lugares em ${uf || ''}`;
		}

		return 'Inicie sua busca';
	});

	function getDisplayValue(
		value: string | number | boolean | Record<string, any>
	): string {
		const suggestion = value as Suggestion;
		const { currentRoute } = router;

		const isSRP =
			currentRoute.value.path.includes('/venda/imoveis') ||
			currentRoute.value.path.includes('/venda/mapa-de-imoveis');

		// Na SRP: retornar vazio para que o placeholder dinâmico seja exibido
		if (isSRP) {
			return '';
		}

		// Para home e outras páginas, mostrar o texto selecionado
		return getSelectText(suggestion);
	}

	async function getLocation(searchParams: SearchParams) {
		if (!searchParams.placeId) {
			return { lat: null, lng: null };
		}

		// Lazily initialize Google Maps when needed
		const googleMapsApi = await initGoogleMaps();

		if (!googleMapsApi?.places) {
			return { lat: null, lng: null };
		}

		const { Place } = googleMapsApi.places;

		const place = new Place({
			id: searchParams.placeId
		});

		await place.fetchFields({
			fields: ['location']
		});

		return {
			lat: place.location?.lat() || null,
			lng: place.location?.lng() || null
		};
	}

	function getSelectText({
		searchType,
		result: { title, subtitle }
	}: Suggestion): string {
		if (
			props.regionSelectTextFormat === 'region-comma' &&
			searchType === 'region'
		) {
			return [title, subtitle].filter(Boolean).join(', ');
		}

		if (['region', 'macroregion', 'city'].includes(searchType)) {
			return [title, subtitle].filter(Boolean).join(' - ');
		}

		return [title, subtitle].filter(Boolean).join(', ');
	}

	const { selectedMacroregion: globalSelectedMacroregion } =
		useTwoFieldSearchSuggestState();

	async function handleModelValueUpdate(
		suggestion: Suggestion,
		shouldUpdateInternalState: boolean = true
	) {
		if (!suggestion) return;

		emit('close-modal');
		await nextTick();

		if (suggestion.macroregionUsed) {
			globalSelectedMacroregion.value = suggestion.macroregionUsed;
		}

		if (shouldUpdateInternalState) {
			hasSelectedValue.value = true;
			updateResearchs(suggestion);
		}

		if (['region', 'macroregion', 'city'].includes(suggestion.searchType)) {
			const text = getSelectText(suggestion);

			const searchParamsWithStates = ensureStatesInSearchParams(suggestion);

			const payload: SelectAddressEvent = {
				...searchParamsWithStates,
				text,
				searchType: suggestion.searchType,
				macroregionUsed: suggestion.macroregionUsed,
				title: suggestion.result.title,
				subtitle: suggestion.result.subtitle
			};

			if (selectOnly) {
				emit('select-address', payload);
			} else {
				bus.emit({ type: 'address', payload });
			}
		}

		if (suggestion.searchType === 'street') {
			const text = getSelectText(suggestion);

			const searchParamsWithStates = ensureStatesInSearchParams(suggestion);
			const { lat, lng } = await getLocation(suggestion.searchParams);

			const payload: SelectAddressEvent = {
				regions: [],
				cities: [],
				macroregions: [],
				condoSlug: null,
				code: null,
				lat: lat ?? undefined,
				lng: lng ?? undefined,
				text,
				searchType: suggestion.searchType,
				states: searchParamsWithStates.states,
				macroregionUsed: suggestion.macroregionUsed,
				title: suggestion.result.title,
				subtitle: suggestion.result.subtitle
			};

			if (selectOnly) {
				emit('select-address', payload);
			} else {
				bus.emit({ type: 'address', payload });
			}
		}

		if (suggestion.searchType === 'condo') {
			const searchParamsWithStates = ensureStatesInSearchParams(suggestion);
			const text = getSelectText(suggestion);

			if (shouldUpdateInternalState) {
				isUpdatingFromWatch.value = true;
				searchTerm.value = text;
			}

			const payload = {
				...searchParamsWithStates,
				condoSlug: searchParamsWithStates.condoSlug,
				condoName: suggestion.result.title,
				textSearched: text
			};

			if (selectOnly) {
				emit('select-condo', payload);
			} else {
				bus.emit({ type: 'condo', payload });
			}
		}

		if (suggestion.searchType === 'point_of_interest') {
			const text = getSelectText(suggestion);

			if (shouldUpdateInternalState) {
				isUpdatingFromWatch.value = true;
				searchTerm.value = text;
			}

			const { lat, lng } = await getLocation(suggestion.searchParams);
			const searchParamsWithStates = ensureStatesInSearchParams(suggestion);

			const payload: SelectAddressEvent = {
				regions: [],
				cities: [],
				macroregions: [],
				condoSlug: null,
				code: null,
				lat: lat ?? undefined,
				lng: lng ?? undefined,
				text: getSelectText(suggestion),
				searchType: suggestion.searchType,
				states: searchParamsWithStates.states,
				macroregionUsed: suggestion.macroregionUsed,
				title: suggestion.result.title,
				subtitle: suggestion.result.subtitle
			};

			if (selectOnly) {
				emit('select-place', payload);
			} else {
				bus.emit({ type: 'place', payload });
			}
		}

		if (suggestion.searchType === 'code') {
			const text = getSelectText(suggestion);

			if (shouldUpdateInternalState) {
				isUpdatingFromWatch.value = true;
				searchTerm.value = text;
			}

			if (selectOnly) {
				emit('select-code', suggestion.searchParams);
			} else {
				bus.emit({
					type: 'code',
					payload: suggestion.searchParams
				});
			}
		}
	}

	const handleApplySearchTerm = () => {
		if (props.searchTerm) {
			isUpdatingFromWatch.value = true;
			searchTerm.value = props.searchTerm || '';

			const suggestion: Suggestion = {
				result: {
					title: props.searchTerm,
					subtitle: ''
				},
				id: uuidv4(),
				searchParams: {
					code: null,
					regions: [],
					cities: [],
					macroregions: [],
					placeId: null,
					condoSlug: null,
					text: props.searchTerm
				},
				searchType: 'street'
			};

			modelValue.value = suggestion;
		}
	};

	function setInitialSelection(selectedValue: Ref<any>) {
		if (isUpdatingFromWatch.value) return;

		if (searchTerm.value.length < 3) return;

		const groupPriority = [
			'cities',
			'regions',
			'pointsOfInterest',
			'streets',
			'condos'
		];

		for (const groupKey of groupPriority) {
			const group = locationAutocompleteData.value?.find(
				(group: SuggestionGroup) =>
					group.key === groupKey &&
					group.visible &&
					group.suggestions.length > 0
			);

			if (group?.suggestions.length) {
				const firstSuggestion = group.suggestions[0];
				if (firstSuggestion) {
					selectedValue.value = {
						heading: group.heading,
						key: group.key,
						visible: true,
						suggestions: [firstSuggestion]
					};
					return;
				}
			}
		}

		if (searchByCodeOption.value.length) {
			const firstSuggestion = searchByCodeOption.value[0];
			if (firstSuggestion) {
				selectedValue.value = {
					heading: {
						text: 'Código',
						icon: null
					},
					key: 'code' as any,
					visible: true,
					suggestions: [firstSuggestion]
				};
			}
		}
	}

	const MIN_SEARCH_LENGTH = 3;

	// Sync watch to reset hasSelectedValue immediately when user types (prevents race conditions)
	watch(
		searchTerm,
		(newValue, oldValue) => {
			emit('update:search-term', newValue);

			// Skip programmatic updates
			if (isUpdatingFromWatch.value) {
				isUpdatingFromWatch.value = false;
				isInternalLoading.value = false;
				return;
			}

			if (isTransitionBlocked.value) {
				isTransitionBlocked.value = false;
				isInternalLoading.value = false;
				return;
			}

			// User is typing if oldValue exists (not initial render)
			const isUserTyping = oldValue !== undefined && oldValue !== newValue;

			if (hasSelectedValue.value && isUserTyping) {
				hasSelectedValue.value = false;
			}

			if (newValue.length >= MIN_SEARCH_LENGTH && isUserTyping) {
				isInternalLoading.value = true;
			} else if (newValue.length < MIN_SEARCH_LENGTH) {
				isInternalLoading.value = false;
			}
		},
		{ immediate: true, flush: 'sync' }
	);

	// Reset loading when API completes or value is selected
	watch([apiLoading, hasSelectedValue], ([loading, selected]) => {
		if (!loading || selected) {
			isInternalLoading.value = false;
		}
	});

	if (isDropdownOpen) {
		watch(isDropdownOpen, isOpen => {
			if (isOpen) {
				isTransitionBlocked.value = true;
				isInternalLoading.value = false;
			}
		});
	}

	const internalPlaceholders = ref(props.placeholders);

	watch(
		() => props.placeholders,
		newPlaceholders => {
			internalPlaceholders.value = newPlaceholders;
		}
	);

	return {
		// State
		searchTerm,
		modelValue,
		commandEl,
		hasSelectedValue,
		isNavigatingWithKeyboard,
		isUpdatingFromWatch,
		backupSearchTerm,
		isInternalLoading,
		isTransitionBlocked,

		// Computed
		hasMinSearchTerm,
		searchByCodeOption,
		shouldShowCodeGroup,
		shouldShowEmptyState,
		shouldShowLastResearchs,
		locationAutocompleteData,
		loading,
		dynamicPlaceholder,

		// Research suggestions
		filteredLastResearchs,
		removeResearchs,

		// Props pass-through for styling
		placeholders: computed({
			get: () => internalPlaceholders.value,
			set: value => {
				internalPlaceholders.value = value;
			}
		}),
		inputWrapperClasses: computed(() => props.inputWrapperClasses),
		inputClasses: computed(() => props.inputClasses),
		inputClearIconWrapperClasses: computed(
			() => props.inputClearIconWrapperClasses
		),
		inputClearIconClasses: computed(() => props.inputClearIconClasses),
		inputSearchIconWrapperClasses: computed(
			() => props.inputSearchIconWrapperClasses
		),
		inputSearchIconClasses: computed(() => props.inputSearchIconClasses),
		emptyStateWrapperClasses: computed(() => props.emptyStateWrapperClasses),
		emptyStateIconWrapperClasses: computed(
			() => props.emptyStateIconWrapperClasses
		),
		emptyStateIconClasses: computed(() => props.emptyStateIconClasses),
		emptyStateTitleClasses: computed(() => props.emptyStateTitleClasses),
		emptyStateDescriptionClasses: computed(
			() => props.emptyStateDescriptionClasses
		),
		groupWrapperClasses: computed(() => props.groupWrapperClasses),
		groupHeadingClasses: computed(() => props.groupHeadingClasses),
		groupIconClasses: computed(() => props.groupIconClasses),
		groupItemWrapperClasses: computed(() => props.groupItemWrapperClasses),
		groupItemTextClasses: computed(() => props.groupItemTextClasses),
		groupItemSubTextClasses: computed(() => props.groupItemSubTextClasses),

		// Functions
		clear,
		clearLocationData,
		handleKeyDown,
		getDisplayValue,
		handleModelValueUpdate,
		handleApplySearchTerm,
		executeLocationAutocomplete,
		setInitialSelection
	};
}
